import { FC, useEffect, useMemo, useRef, useState } from 'react'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { ReactComponent as ChevronIcon } from 'src/assets/svg/chevron.svg'
import { Card } from 'src/components/Card'
import { platformDisplayLabel } from 'src/constants/platformLabels'
import { OperatorFilterDropdown } from 'src/components/OperatorFilterDropdown'
import { SectionTitle } from 'src/components/SectionTitle'
import { ScrollBarContainer } from 'src/components/ScrollBarContainer'
import { HeaderCell } from 'src/components/table/HeaderCell'
import { NameCell } from 'src/components/table/NameCell'
import { BorderedRow } from 'src/components/table/BorderedRow'
import { SentimentByMostRated } from 'src/components/table/SentimentByMostRated'
import { MentionCell } from 'src/components/table/MentionCell'
import { StickyHead } from 'src/components/table/StickyHead'
import { Skeleton } from 'src/components/Skeleton'
import { numberWithCommas } from 'src/utils/numberFormat'
import useOutsideClick from 'src/hooks/useOutsideClick'
import { useGetTopPhrasesQuery } from 'src/reactQuery/useGetTopPhrasesQuery'
import { PhraseData } from 'src/services/__generated__/api'
import en from 'src/constants/en'
import styles from './topPhrases.module.scss'

// Pretty labels for known platform ids; anything else is title-cased.
const PLATFORM_LABEL_MAP: Record<string, string> = {
  booking: 'Booking',
  expedia: 'Expedia',
  facebook: 'Facebook',
  instagram: 'Instagram',
  whatsapp: 'WhatsApp',
}

const platformLabel = (id: string) =>
  PLATFORM_LABEL_MAP[id] ??
  platformDisplayLabel(id, id.charAt(0).toUpperCase() + id.slice(1))

const SENTIMENT_TABS = [
  { key: 'all', label: 'All' },
  { key: 'positive', label: 'Positive' },
  { key: 'negative', label: 'Negative' },
  { key: 'neutral', label: 'Neutral' },
] as const

const VISIBLE_ROW_COUNT = 6

// The skeleton rows reuse the real <table> and the real BorderedRow, so the
// 20px cell padding and the column widths come from the live CSS rather than
// from guesses here. Only the cell content height is stated: NameCell is 16px
// text (nameCell.module.scss), so a 20px block fills the same line box.
const SKELETON_CELL_HEIGHT = 20
const SKELETON_COLUMN_COUNT = 6

interface TopPhrasesProps {
  fromDate?: string
  isLoading?: boolean
  pageId?: string
  toDate?: string
}

const PhraseNameCell: FC<{ value: string }> = ({ value }) => {
  const ref = useRef<HTMLSpanElement>(null)
  const [isTruncated, setIsTruncated] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return undefined

    const check = () => setIsTruncated(el.scrollWidth > el.clientWidth)
    check()

    const observer = new ResizeObserver(check)
    observer.observe(el)
    return () => observer.disconnect()
  }, [value])

  return (
    <NameCell>
      <span className={styles.phraseCellWrap}>
        <span className={styles.phraseCell} ref={ref}>
          {value}
        </span>
        {isTruncated && <span className={styles.phraseTooltip}>{value}</span>}
      </span>
    </NameCell>
  )
}

export const TopPhrases: FC<TopPhrasesProps> = ({
  isLoading: externalLoading,
  pageId = '',
  fromDate,
  toDate,
}) => {
  const [activeTab, setActiveTab] = useState<'customers' | 'operators'>(
    'customers'
  )
  const [activePlatform, setActivePlatform] = useState<string>('all')
  const [isPlatformOpen, setIsPlatformOpen] = useState(false)
  // null = all operators; [] = none; [ids] = subset (mirrors Inbox Preview).
  const [selectedOperators, setSelectedOperators] = useState<string[] | null>(
    null
  )
  const [activeSentiment, setActiveSentiment] = useState<string>('all')
  const [isExpanded, setIsExpanded] = useState(false)

  const platformDropdownRef = useRef<HTMLDivElement>(null)
  useOutsideClick({
    onOutsideClick: () => setIsPlatformOpen(false),
    ref: platformDropdownRef,
  })

  // "__none__" is a sentinel that matches no operator, so deselecting all
  // returns no phrases rather than silently falling back to every operator.
  const operatorsParam =
    selectedOperators === null
      ? undefined
      : selectedOperators.length
        ? selectedOperators.join(',')
        : '__none__'

  const { data: apiData, isLoading: apiLoading } = useGetTopPhrasesQuery({
    fromDate,
    operators: operatorsParam,
    pageId,
    platforms: activePlatform !== 'all' ? activePlatform : undefined,
    toDate,
    type: activeTab,
  })

  const isLoading = externalLoading || apiLoading

  // First load only. `phrases` is [] before the response lands, which is
  // indistinguishable from genuinely having none, so the table slot has to say
  // "loading" structurally instead of dropping to the 200px empty state and
  // then jumping back open when rows arrive.
  const isInitialLoading = !!isLoading && !apiData

  const phrases = apiData?.phrases || []

  // Dropdown options come from the backend facets, so only platforms/operators
  // that actually exist for this page are listed.
  const platformOptions = useMemo(
    () => [
      { id: 'all', label: 'All platforms' },
      ...(apiData?.facets?.platforms ?? []).map((id) => ({
        id,
        label: platformLabel(id),
      })),
    ],
    [apiData?.facets?.platforms]
  )

  const operatorOptions = useMemo(
    () => apiData?.facets?.operators ?? [],
    [apiData?.facets?.operators]
  )

  const filteredPhrases = useMemo(
    () =>
      activeSentiment === 'all'
        ? phrases
        : phrases.filter(
            (phrase) =>
              (phrase.mostlyRatedAs || '').toLowerCase() === activeSentiment
          ),
    [phrases, activeSentiment]
  )

  const visiblePhrases = useMemo(
    () =>
      isExpanded
        ? filteredPhrases
        : filteredPhrases.slice(0, VISIBLE_ROW_COUNT),
    [filteredPhrases, isExpanded]
  )

  const activePlatformLabel =
    platformOptions.find((platform) => platform.id === activePlatform)?.label ??
    'All platforms'

  const columnHelper = createColumnHelper<PhraseData>()

  const columns = useMemo(
    () => [
      columnHelper.accessor('phrase', {
        cell: (info) => <PhraseNameCell value={info.getValue() || ''} />,
        header: () => <HeaderCell>Phrase</HeaderCell>,
        id: 'phrase',
      }),
      columnHelper.accessor('mostlyRatedAs', {
        cell: (info) => (
          <SentimentByMostRated sentiment={info.getValue() || ''} />
        ),
        header: () => <HeaderCell>{en.mostlyRatedAs}</HeaderCell>,
        id: 'mostlyRatedAs',
      }),
      columnHelper.accessor('positiveMentions', {
        cell: (info) => {
          const { row } = info
          return (
            <MentionCell
              percentage={row.original.positivePercentage}
              value={info.getValue()}
            />
          )
        },
        header: () => <HeaderCell>Positive Messages</HeaderCell>,
        id: 'positiveMentions',
      }),
      columnHelper.accessor('neutralMentions', {
        cell: (info) => {
          const { row } = info
          return (
            <MentionCell
              percentage={row.original.neutralPercentage}
              value={info.getValue()}
            />
          )
        },
        header: () => <HeaderCell>Neutral Messages</HeaderCell>,
        id: 'neutralMentions',
      }),
      columnHelper.accessor('negativeMentions', {
        cell: (info) => {
          const { row } = info
          return (
            <MentionCell
              percentage={row.original.negativePercentage}
              value={info.getValue()}
            />
          )
        },
        header: () => <HeaderCell>Negative Messages</HeaderCell>,
        id: 'negativeMentions',
      }),
      columnHelper.accessor('totalMentions', {
        cell: (info) => (
          <NameCell>{numberWithCommas(info.getValue() || 0)}</NameCell>
        ),
        header: () => <HeaderCell>Total Messages</HeaderCell>,
        id: 'totalMentions',
      }),
    ],
    []
  )

  const { getHeaderGroups, getRowModel } = useReactTable({
    columns,
    data: visiblePhrases,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <Card className={styles.topPhrases}>
      <SectionTitle
        endItem={
          <div className={styles.tabsContainer}>
            <button
              className={`${styles.tab} ${
                activeTab === 'customers' ? styles.active : ''
              }`}
              onClick={() => setActiveTab('customers')}
            >
              Customers
            </button>
            <button
              className={`${styles.tab} ${
                activeTab === 'operators' ? styles.active : ''
              }`}
              onClick={() => setActiveTab('operators')}
            >
              Operators
            </button>
          </div>
        }
        isLoading={isLoading}
        title="Frequently Used Phrases"
        tooltipContent="Most frequently used phrases across conversations"
      />

      <div className={styles.filterRow}>
        <div className={styles.platformDropdownWrap} ref={platformDropdownRef}>
          <button
            className={styles.platformDropdownButton}
            onClick={() => setIsPlatformOpen((open) => !open)}
            type="button"
          >
            {activePlatformLabel}
            <ChevronIcon
              className={`${styles.platformDropdownIcon} ${
                isPlatformOpen ? styles.platformDropdownIconOpen : ''
              }`}
            />
          </button>

          {isPlatformOpen && (
            <div className={styles.platformDropdownPanel}>
              {platformOptions.map((platform) => (
                <button
                  className={`${styles.platformDropdownOption} ${
                    activePlatform === platform.id
                      ? styles.platformDropdownOptionActive
                      : ''
                  }`}
                  key={platform.id}
                  onClick={() => {
                    setActivePlatform(platform.id)
                    setIsPlatformOpen(false)
                  }}
                  type="button"
                >
                  {platform.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <OperatorFilterDropdown
          onChange={setSelectedOperators}
          operators={operatorOptions}
          selected={selectedOperators}
        />

        <div className={styles.sentimentTabs}>
          {SENTIMENT_TABS.map((tab) => (
            <button
              className={`${styles.sentimentTab} ${
                activeSentiment === tab.key ? styles.sentimentTabActive : ''
              }`}
              key={tab.key}
              onClick={() => {
                setActiveSentiment(tab.key)
                setIsExpanded(false)
              }}
              type="button"
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.tableContent}>
        {isInitialLoading ? (
          <ScrollBarContainer className={styles.tableContainer}>
            <table width="100%">
              <StickyHead>
                {getHeaderGroups().map((headerGroup) => (
                  <BorderedRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th align="left" key={header.id}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </BorderedRow>
                ))}
              </StickyHead>

              <tbody>
                {Array.from({ length: VISIBLE_ROW_COUNT }).map(
                  (_, rowIndex) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <BorderedRow key={rowIndex}>
                      {Array.from({ length: SKELETON_COLUMN_COUNT }).map(
                        (__, cellIndex) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <td align="left" key={cellIndex}>
                            <Skeleton height={SKELETON_CELL_HEIGHT} />
                          </td>
                        )
                      )}
                    </BorderedRow>
                  )
                )}
              </tbody>
            </table>
          </ScrollBarContainer>
        ) : filteredPhrases.length ? (
          <ScrollBarContainer className={styles.tableContainer}>
            <table width="100%">
              <StickyHead>
                {getHeaderGroups().map((headerGroup) => (
                  <BorderedRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th align="left" key={header.id}>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </BorderedRow>
                ))}
              </StickyHead>

              <tbody>
                {getRowModel().rows.map((row) => (
                  <BorderedRow key={row.id}>
                    {row.getVisibleCells().map((cell) => (
                      <td align="left" key={cell.id}>
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </BorderedRow>
                ))}
              </tbody>
            </table>
          </ScrollBarContainer>
        ) : (
          <div className={styles.emptyState}>
            <div>{isLoading ? en.loading : en.notEnoughDataToDisplay}</div>
          </div>
        )}
      </div>

      {filteredPhrases.length > VISIBLE_ROW_COUNT && (
        <button
          className={styles.seeAllButton}
          onClick={() => setIsExpanded((expanded) => !expanded)}
          type="button"
        >
          {isExpanded ? 'See less' : 'See all'}
          <ChevronIcon
            className={`${styles.seeAllIcon} ${
              isExpanded ? styles.seeAllIconOpen : ''
            }`}
          />
        </button>
      )}
    </Card>
  )
}
